// ============================================
// NRS.08 — Session State & Handoff Service
// ============================================
// Extended from: src/services/patientJourney/sessionState.ts
// Spec: hlx-nrs-08-session-state-handoff.md (LOCKED)
// Contract: Atomic Firestore persist, event emission,
//           gap detection (5-min), concurrent tab detection,
//           network retry (exponential backoff, max 3)
//           Canonical path: sessions/{sessionId}/safetyProfile (FD-NRS-1)
// ============================================

import type {
  SafetyDataProfile,
  SafetyDataSource,
  PersistedSessionState,
  NurseCompletionEvent,
  SessionResumeEvent,
  GapDetectionConfig,
  RetryConfig,
  SessionHandoffMetrics,
} from '../types/sessionStateHandoff';
import { DEFAULT_GAP_DETECTION, DEFAULT_RETRY_CONFIG } from '../types/sessionStateHandoff';
import type { DataCompletenessFlags } from '../types/triageSession';
import { INITIAL_DATA_COMPLETENESS_FLAGS } from '../types/triageSession';

// ── Metrics State ──

let _metrics: SessionHandoffMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};
let _totalLatency = 0;
let _successCount = 0;

// ── Injectable Interface ──

export interface SessionFirestoreProvider {
  atomicPersist(
    sessionId: string,
    state: PersistedSessionState,
  ): Promise<void>;

  writeEvent(
    path: string,
    event: NurseCompletionEvent | SessionResumeEvent,
  ): Promise<void>;

  getLastActiveAt(sessionId: string): Promise<Date | null>;
  setLastActiveAt(sessionId: string, timestamp: Date): Promise<void>;
}

// ── Safety Data Profile Builder ──

export function createEmptySafetyDataProfile(): SafetyDataProfile {
  return {
    sex: undefined,
    age: undefined,
    modalMedications: [],
    chatMedications: [],
    modalAllergies: [],
    chatAllergies: [],
    modalConditions: [],
    chatConditions: [],
    isPregnant: undefined,
    isBreastfeeding: undefined,
    medicationsProvided: false,
    allergiesProvided: false,
    conditionsProvided: false,
    sexProvided: false,
    ageProvided: false,
    pregnancyStatusProvided: false,
    lastUpdated: new Date().toISOString(),
    isTryingToConceive: undefined,
    reproductiveStatus: 'not_applicable',
    sourceLog: [],
  };
}

export function addSourceAttribution(
  profile: SafetyDataProfile,
  field: string,
  source: SafetyDataSource,
  turn?: number,
): SafetyDataProfile {
  return {
    ...profile,
    sourceLog: [
      ...profile.sourceLog,
      { field, source, turn, timestamp: new Date() },
    ],
  };
}

// ── Session Handoff Service ──

export class SessionHandoffService {
  private sessionId: string;
  private patientId: string;
  private firestore: SessionFirestoreProvider;
  private gapConfig: GapDetectionConfig;
  private retryConfig: RetryConfig;
  private safetyProfile: SafetyDataProfile;
  private dataCompletenessFlags: DataCompletenessFlags;
  private lastActiveAt: Date;

  constructor(
    sessionId: string,
    patientId: string,
    firestore: SessionFirestoreProvider,
    gapConfig?: Partial<GapDetectionConfig>,
    retryConfig?: Partial<RetryConfig>,
  ) {
    this.sessionId = sessionId;
    this.patientId = patientId;
    this.firestore = firestore;
    this.gapConfig = { ...DEFAULT_GAP_DETECTION, ...gapConfig };
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...retryConfig };
    this.safetyProfile = createEmptySafetyDataProfile();
    this.dataCompletenessFlags = { ...INITIAL_DATA_COMPLETENESS_FLAGS };
    this.lastActiveAt = new Date();
  }

  /** Update safety data profile from modal submission */
  updateFromModal(
    field: 'medications' | 'allergies' | 'conditions',
    values: string[],
  ): void {
    const modalField = `modal${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof SafetyDataProfile;
    (this.safetyProfile as unknown as Record<string, unknown>)[modalField] = values;
    this.safetyProfile = addSourceAttribution(this.safetyProfile, modalField as string, 'modal');
  }

  /** Update safety data profile from chat disclosure */
  updateFromChat(
    field: 'medications' | 'allergies' | 'conditions',
    values: string[],
    turnNumber: number,
  ): void {
    const chatField = `chat${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof SafetyDataProfile;
    const existing = (this.safetyProfile as unknown as Record<string, unknown>)[chatField] as string[] || [];
    (this.safetyProfile as unknown as Record<string, unknown>)[chatField] = [...existing, ...values];
    this.safetyProfile = addSourceAttribution(this.safetyProfile, chatField as string, 'chat', turnNumber);
    // Chat disclosures do NOT flip DataCompletenessFlags (LOCKED contract)
  }

  /** Update pregnancy status from modal */
  updatePregnancyStatus(
    isPregnant: boolean,
    isTryingToConceive: boolean,
    isBreastfeeding: boolean,
  ): void {
    this.safetyProfile.isPregnant = isPregnant;
    this.safetyProfile.isTryingToConceive = isTryingToConceive;
    this.safetyProfile.isBreastfeeding = isBreastfeeding;
    this.safetyProfile = addSourceAttribution(this.safetyProfile, 'pregnancyStatus', 'modal');
  }

  /** Update data completeness flags from modal orchestrator — syncs onto profile */
  updateDataCompletenessFlags(flags: DataCompletenessFlags): void {
    this.dataCompletenessFlags = { ...flags };
    // Flatten onto SafetyDataProfile for PRO.01 contract
    this.safetyProfile.medicationsProvided = flags.medicationsProvided;
    this.safetyProfile.allergiesProvided = flags.allergiesProvided;
    this.safetyProfile.pregnancyStatusProvided = flags.pregnancyStatusProvided;
    this.safetyProfile.lastUpdated = new Date().toISOString();
  }

  /** Atomic persist to Firestore with retry */
  async persist(
    modifiers: Record<string, unknown>,
    lateReveals: Array<{ turn: number; modifier: string; category: 'safety_critical' | 'preference' }>,
    phase: string,
    turnNumber: number,
    phase1Complete: boolean,
    protocolPresented: boolean,
  ): Promise<void> {
    const startTime = Date.now();
    _metrics.totalCalls++;

    const state: PersistedSessionState = {
      modifiers,
      lateReveals,
      phase,
      turnNumber,
      phase1Complete,
      protocolPresented,
      safetyDataProfile: { ...this.safetyProfile },
      dataCompleteness: { ...this.dataCompletenessFlags },
      lastActiveAt: new Date(),
      updatedAt: new Date(),
    };

    await this.withRetry(() => this.firestore.atomicPersist(this.sessionId, state));

    this.lastActiveAt = state.lastActiveAt;
    _successCount++;
    _totalLatency += Date.now() - startTime;
  }

  /** Emit NurseCompletionEvent (→ PRO.01) */
  async emitCompletionEvent(turnNumber: number): Promise<NurseCompletionEvent> {
    const event: NurseCompletionEvent = {
      eventId: generateId(),
      sessionId: this.sessionId,
      patientId: this.patientId,
      safetyDataProfileComplete: this.isProfileComplete(),
      triggerSource: 'build_protocol_marker',
      dataCompletenessFlags: { ...this.dataCompletenessFlags },
      completedAt: new Date().toISOString(),
      turnNumber,
    };

    const path = `sessions/${this.sessionId}/events/nurse_completion`;
    await this.withRetry(() => this.firestore.writeEvent(path, event));
    return event;
  }

  /** Check for session gap and emit SessionResumeEvent if needed */
  async checkForGap(
    resumeTrigger: 'page_load' | 'auth_restore' | 'tab_focus' | 'explicit_return' = 'page_load',
    protocolId: string | null = null,
  ): Promise<SessionResumeEvent | null> {
    const previousLastActiveAt = await this.firestore.getLastActiveAt(this.sessionId);
    if (!previousLastActiveAt) return null;

    const gapMs = Date.now() - previousLastActiveAt.getTime();
    if (gapMs < this.gapConfig.gapThresholdMs) return null;

    const event: SessionResumeEvent = {
      eventId: generateId(),
      sessionId: this.sessionId,
      patientId: this.patientId,
      protocolId,
      resumeTrigger,
      gapDurationMs: gapMs,
      previousLastActiveAt: previousLastActiveAt.toISOString(),
      resumedAt: new Date().toISOString(),
    };

    const path = `sessions/${this.sessionId}/events/resume_${event.eventId}`;
    await this.withRetry(() => this.firestore.writeEvent(path, event));
    return event;
  }

  /** Update last active timestamp (call on every interaction) */
  async touchActive(): Promise<void> {
    this.lastActiveAt = new Date();
    await this.firestore.setLastActiveAt(this.sessionId, this.lastActiveAt);
  }

  /** Get current safety data profile */
  getSafetyProfile(): Readonly<SafetyDataProfile> {
    return { ...this.safetyProfile };
  }

  /** Get current data completeness flags */
  getDataCompletenessFlags(): Readonly<DataCompletenessFlags> {
    return { ...this.dataCompletenessFlags };
  }

  /** Check if safety data profile has minimum required data */
  isProfileComplete(): boolean {
    // Profile is "complete" when all relevant flags have been set (AND logic)
    // PRO.01 applies conservative defaults (hard blocks) when flags are false
    return this.dataCompletenessFlags.medicationsProvided &&
           this.dataCompletenessFlags.allergiesProvided &&
           this.safetyProfile.pregnancyStatusProvided;
  }

  /** Populate sex/age from triage data into SafetyDataProfile */
  setTriageData(sex: 'male' | 'female' | undefined, age: number | undefined): void {
    this.safetyProfile.sex = sex;
    this.safetyProfile.sexProvided = sex !== undefined;
    this.safetyProfile.age = age;
    this.safetyProfile.ageProvided = age !== undefined;
    if (sex === 'male') {
      // Males skip pregnancy modal — auto-set pregnancyStatusProvided
      this.safetyProfile.pregnancyStatusProvided = true;
    }
    this.safetyProfile.lastUpdated = new Date().toISOString();
    this.safetyProfile = addSourceAttribution(this.safetyProfile, 'triageData', 'triage');
  }

  // ── Private Methods ──

  private async withRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastError: Error | null = null;
    let backoffMs = this.retryConfig.initialBackoffMs;

    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt < this.retryConfig.maxRetries) {
          await sleep(backoffMs);
          backoffMs *= this.retryConfig.backoffMultiplier;
        }
      }
    }

    _metrics.errorsByType['retry_exhausted'] = (_metrics.errorsByType['retry_exhausted'] || 0) + 1;
    throw lastError;
  }
}

// ── Factory ──

export function createSessionHandoff(
  sessionId: string,
  patientId: string,
  firestore: SessionFirestoreProvider,
  gapConfig?: Partial<GapDetectionConfig>,
  retryConfig?: Partial<RetryConfig>,
): SessionHandoffService {
  return new SessionHandoffService(sessionId, patientId, firestore, gapConfig, retryConfig);
}

// ── Helpers ──

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Metrics ──

export function getMetrics(): SessionHandoffMetrics {
  return {
    ..._metrics,
    successRate: _metrics.totalCalls > 0 ? _successCount / _metrics.totalCalls : 1,
    avgLatencyMs: _metrics.totalCalls > 0 ? _totalLatency / _metrics.totalCalls : 0,
  };
}
